import {
  Card,
  Col,
  Descriptions,
  Empty,
  Row,
  Space,
  Spin,
  Switch,
  Table,
  Typography,
  message,
} from 'antd';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { BsBox2 } from 'react-icons/bs';
import { useParams } from 'react-router-dom';

import SharedProductImage from '../../components/productImage';
import { sizeEnumDictionary, weightEnumDictionary } from '../../utils';
import { convertProductVolume, getYmd } from '../../utils/format';
import './Product.less';

const { Text, Title } = Typography;

/** Барааны зурагны хэмжээ (хүснэгт дотор) */
const IMAGE_SIZE = 40;

/** Барааны нэрийн хамгийн их өргөн (урт бол ellipsis + tooltip) */
const TITLE_MAX_WIDTH = 320;

/** Хоосон утгыг "-" болгож харуулна */
const val = (value) => (value === null || value === undefined || value === '' ? '-' : value);

/** Descriptions-ийн ерөнхий тохиргоо: label дээр, value доор, зай багатай */
const descriptionProps = {
  size: 'small',
  colon: false,
  layout: 'vertical',
  column: { xs: 1, sm: 2 },
  labelStyle: { paddingBottom: 0 },
  contentStyle: { paddingBottom: 8 },
};

/** Картын толгой: дүрс + текст */
const CardTitle = ({ icon: Icon, children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
    <Icon style={{ display: 'block', fontSize: '1em' }} />
    {children}
  </span>
);

/** Барааны зураг: зураггүй / ачаалагдаагүй үед icon */
const ProductImage = ({ src, alt = 'Барааны зураг', size = IMAGE_SIZE, preview = false }) => (
  <SharedProductImage
    src={src}
    alt={alt}
    size={size}
    preview={preview}
    style={{ objectFit: 'cover', borderRadius: 4, boxShadow: '2px 2px 4px 0px #00000040' }}
  />
);

/** Хоёр мөр текст: дээр үндсэн, доор нэмэлт (жижиг, сааралдуу) */
const TwoLineText = ({ primary, secondary, maxWidth }) => (
  <Space direction="vertical" size={0} style={{ maxWidth }}>
    <Text style={{ maxWidth }} ellipsis={maxWidth ? { tooltip: primary } : false}>
      {primary}
    </Text>
    <Text type="secondary" style={{ fontSize: 12, maxWidth }} ellipsis>
      {secondary}
    </Text>
  </Space>
);

/** Дэд барааны хүснэгт — Захиалгын дэлгэрэнгүй дэх барааны хүснэгттэй ижил төрх */
const SubProductTable = ({ products = [] }) => {
  const columns = [
    {
      key: 'index',
      title: '№',
      width: 48,
      align: 'center',
      render: (_, record, index) => <Text type="secondary">{index + 1}</Text>,
    },
    {
      key: 'product',
      title: 'Бараа',
      render: (_, record) => (
        <Space size={12} align="center">
          <ProductImage src={record?.imageCover} alt={record?.title} />
          <TwoLineText
            primary={val(record?.title)}
            secondary={val(record?.productSku)}
            maxWidth={TITLE_MAX_WIDTH}
          />
        </Space>
      ),
    },
    {
      key: 'stock',
      title: 'Тоо ширхэг',
      align: 'center',
      render: (_, record) => (record?.stock ? `${record.stock} ширхэг` : '-'),
    },
  ];

  if (products.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Дэд бараа байхгүй байна."
        style={{ margin: '16px 0' }}
      />
    );
  }

  return (
    <Table
      className="product__group__table"
      size="small"
      columns={columns}
      dataSource={products}
      pagination={false}
      rowKey={(record, index) => record?._id ?? index}
      scroll={{ x: 'max-content' }}
    />
  );
};

const Detail = () => {
  const { productId } = useParams();

  const [checked, setChecked] = useState(false);
  const [detail, setDetail] = useState({});
  const [subProducts, setSubProducts] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    if (productId) {
      getProductDetail(productId);
    }
    //eslint-disable-next-line
  }, [productId]);

  const getProductDetail = (id) => {
    if (id === undefined) return;
    setDetailLoading(true);
    axios
      .get(`/api/v1.0/product/${id}?raw=true`)
      .then(({ data = {} }) => {
        if (data.statusCode === 200) {
          setDetail(data?.result);
          setSubProducts(data?.result?.group);
          if (data.result.status === 'ready') {
            setChecked(true);
          } else if (data.result.status === 'unavailable') {
            setChecked(false);
          }
        }
      })
      .catch((err) => {
        console.error('Product group detail fetch error: ', err);
      })
      .finally(() => setDetailLoading(false));
  };

  const changeProductStatus = (value) => {
    setToggling(true);
    if (value) {
      axios
        .post(`/api/v1.0/product/activate`, {
          productId: detail?._id,
        })
        .then(({ data }) => {
          message.success('Бараа идэвхтэй боллоо.');
          //loadDataFirst();
        })
        .finally(() => setToggling(false));
      setChecked(true);
    } else {
      axios
        .post(`/api/v1.0/product/deactivate`, {
          productId: detail?._id,
        })
        .then(({ data }) => {
          message.success('Бараа идэвхгүй боллоо.');
          getProductDetail(detail?.id);
        })
        .finally(() => setToggling(false));
      setChecked(false);
    }
  };

  /** Эзлэхүүн: тоо + хэмжих нэгж (см³) */
  const volume = (
    <span style={{ textTransform: 'lowercase' }}>
      {convertProductVolume(detail?.volume || 0, detail?.sizeMeasure)}
      {sizeEnumDictionary[detail?.sizeMeasure] ?? 'см'}
      <sup>3</sup>
    </span>
  );

  /** Жин: тоо + хэмжих нэгж */
  const weight = (
    <span style={{ textTransform: 'lowercase' }}>
      {convertProductVolume(detail?.weight || 0, detail?.weightMeasure)}
      {weightEnumDictionary[detail?.weightMeasure] ?? 'г'}
    </span>
  );

  return (
    <Spin spinning={detailLoading}>
      <Space direction="vertical" size={16} style={{ width: '100%', padding: 16 }}>
        <Card
          size="middle"
          title={
            <Title level={5} style={{ margin: 0 }}>
              {val(detail?.title)}
            </Title>
          }
          extra={
            detail?.status ? (
              <Space size={8}>
                <Text type="secondary">Идэвхтэй эсэх</Text>
                <Switch checked={checked} loading={toggling} onChange={changeProductStatus} />
              </Space>
            ) : null
          }
        >
          <Descriptions
            {...descriptionProps}
            column={{ xs: 1, sm: 3 }}
            items={[
              {
                key: 'title',
                label: 'Барааны нэр',
                children: val(detail?.title),
              },
              {
                key: 'sku',
                label: 'SKU',
                children: val(detail?.productSku),
              },
            ]}
          />
        </Card>

        <Card
          title={<CardTitle icon={AiOutlineInfoCircle}>Ерөнхий мэдээлэл</CardTitle>}
          size="middle"
        >
          <Row gutter={[16, 16]} align="top">
            <Col flex="none">
              <ProductImage src={detail?.imageCover} alt={detail?.title} size={120} preview={!!detail?.imageCover} />
            </Col>
            <Col flex="auto" style={{ minWidth: 240 }}>
              <Descriptions
                {...descriptionProps}
                column={{ xs: 1, sm: 2, xl: 4 }}
                items={[
                  { key: 'volume', label: 'Эзлэхүүн', children: volume },
                  { key: 'weight', label: 'Жин', children: weight },
                  { key: 'color', label: 'Өнгө', children: val(detail?.attributes?.color) },
                  { key: 'type', label: 'Төрөл', children: val(detail?.attributes?.type) },
                  { key: 'size', label: 'Хэмжээ', children: val(detail?.attributes?.size) },
                  { key: 'category', label: 'Категори', children: val(detail?.category) },
                  {
                    key: 'returnable',
                    label: 'Буцаагдах эсэх',
                    children: detail?.isReturnable === true ? 'Тийм' : 'Үгүй',
                  },
                  {
                    key: 'expiry',
                    label: 'Дуусах хугацаа',
                    children: val(detail?.expiryDate ? getYmd(detail.expiryDate) : null),
                  },
                ]}
              />
            </Col>
          </Row>
        </Card>

        <Card
          title={<CardTitle icon={BsBox2}>Дэд бараанууд</CardTitle>}
          size="middle"
          styles={{ body: { padding: 0 } }}
        >
          <SubProductTable products={subProducts ?? []} />
        </Card>
      </Space>
    </Spin>
  );
};

export default Detail;
